import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { match } from "ts-pattern";
import { endOfDay, format, startOfDay } from "date-fns";
import { AssetCameraKey } from "@visioni/a3s-schema";
import {
  listCompilationsEndpoint,
  type CompilationItem,
  type CompilationStatus,
  type ListCompilationsResponse,
} from "../../client-messages/compilation";
import type { CompilationListItem } from "../components/CompilationList";
import type { CompilationRowStatus } from "../components/CompilationRow";
import { failureReasonLabel } from "../helpers/failure-reason-label";

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface UseCompilationsListOptions {
  /**
   * Every filter below is applied by the database, so a page is only short when
   * the results genuinely ran out — no client-side filtering pass, and no
   * pages arriving empty because their contents were discarded here.
   */
  status?: CompilationStatus | undefined;
  siteId?: string | undefined;
  cameraKey?: string | undefined;
  dateRange?: { start: Date | null; end: Date | null } | undefined;
  /**
   * Map of camera key → display name. This hook has no camera-name source of
   * its own — the wire item only carries `subject.camera` (a raw key) — so
   * the caller supplies names it already has (e.g. from its own camera list),
   * mirroring `use-activity-list.ts`'s `cameraNames` option.
   */
  cameraNames?: ReadonlyMap<string, string> | undefined;
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export interface CompilationsListResult {
  readonly items: readonly CompilationListItem[];
  /** Total matching the filters, not just those loaded so far. */
  readonly totalCount: number;
  readonly isLoading: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly error: Error | null;
  readonly fetchNextPage: () => void;
}

// ---------------------------------------------------------------------------
// View model mapping
// ---------------------------------------------------------------------------

/** Rows fetched per page. */
const PAGE_SIZE = 50;

/**
 * Resolves the picked date range to the absolute instants the endpoint filters
 * on.
 *
 * This has to happen here rather than server-side: the picker yields
 * start-of-day for *both* ends, and only the client knows which timezone those
 * days belong to — the Lambda runs in UTC. Sending a bare calendar date for
 * either bound would have it resolved against the wrong day, dropping late-day
 * footage west of UTC and pulling in the next local morning east of it.
 */
export function toDateBounds(dateRange?: { start: Date | null; end: Date | null }): {
  readonly dateFrom: string | undefined;
  readonly dateTo: string | undefined;
} {
  return {
    dateFrom: dateRange?.start ? startOfDay(dateRange.start).toISOString() : undefined,
    dateTo: dateRange?.end ? endOfDay(dateRange.end).toISOString() : undefined,
  };
}

/**
 * A `queued` job stuck this long with nothing else queued behind it on the
 * same camera is presumed dead rather than merely slow — legitimate delays
 * (edge reboot, IoT retry/backoff, resource contention) resolve well inside
 * this window. This only ever surfaces as a warning alongside the `queued`
 * status, never as a status of its own: the underlying record stays `queued`
 * server-side, so the warning clears itself the moment the job progresses.
 */
export const STALE_QUEUED_THRESHOLD_MS = 30 * 60 * 1000;

/**
 * A `queued` job isn't presumed dead if another job is queued behind it for
 * the same camera — that job is legitimately waiting its turn, however long
 * the wait looks from the outside.
 */
export function isStaleQueued(item: CompilationItem, now: number): boolean {
  if (item.status !== "queued") return false;
  // Counted by the endpoint, not here: filters and paging mean the siblings
  // may not be among the rows this hook holds.
  if ((item.queuedSiblings ?? 0) > 0) return false;
  return now - new Date(item.requestedAt).getTime() > STALE_QUEUED_THRESHOLD_MS;
}

/**
 * Maps the wire status to the row's display status. A `ready` job without a
 * signed URL (shouldn't happen — the endpoint signs one whenever status is
 * `ready` — but the schema makes `video` optional) is shown as `expired`
 * rather than a broken "ready" row with nothing to play.
 *
 * Otherwise the wire status is shown as-is. A stale `queued` job stays
 * `queued` and is flagged via {@link CompilationListItem.staleQueued}
 * instead. Relabelling it `failed` here would put the row out of step with
 * the status filter the moment that filter is served from the store rather
 * than from this mapped list: "Failed" would exclude the very rows the list
 * calls failed, and "Queued" would return rows it calls something else.
 * Staleness is a presentation concern, so it rides alongside the status
 * instead of overwriting it.
 */
export function toRowStatus(item: CompilationItem): CompilationRowStatus {
  return match(item.status)
    .with("queued", () => "queued" as const)
    .with("processing", () => "processing" as const)
    .with("uploading", () => "uploading" as const)
    .with("failed", () => "failed" as const)
    .with("ready", () => (item.video ? ("ready" as const) : ("expired" as const)))
    .exhaustive();
}

/** Display format for the row's date/time fields, e.g. "17 Jun 2026, 2:00pm". */
function formatDisplayDate(isoString: string): string {
  return format(new Date(isoString), "d MMM yyyy, h:mmaaa");
}

export function toViewModel(
  item: CompilationItem,
  cameraNames?: ReadonlyMap<string, string>,
  options?: { readonly stale?: boolean },
): CompilationListItem {
  const cameraKey = AssetCameraKey.toString(item.subject.camera);
  return {
    id: item.requestId,
    status: toRowStatus(item),
    staleQueued: options?.stale ?? false,
    failureReason: failureReasonLabel(item.failureReason),
    cameraLabel: cameraNames?.get(cameraKey) ?? cameraKey,
    requestedAt: formatDisplayDate(item.requestedAt),
    rangeStart: formatDisplayDate(item.startTime),
    rangeEnd: formatDisplayDate(item.endTime),
    videoUrl: item.video?.url,
    // Left undefined (rather than falling back to `url`) for a Lambda not yet
    // redeployed with the download-signing change — the download button
    // disables itself when this is absent (see `CompilationRow`).
    downloadUrl: item.video?.downloadUrl,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Paginated list of compilation jobs for the current tenant.
 *
 * Mirrors `features/a3s-activity/src/app/hooks/use-activity-list.ts` — same
 * `useInfiniteQuery` shape over the same offset/limit contract, since the two
 * back near-identical screens. `hasNextPage` compares rows loaded against the
 * `totalCount` the endpoint reports, which is exact because every filter is
 * applied by the database.
 */
export function useCompilationsList(options: UseCompilationsListOptions = {}): CompilationsListResult {
  const apiConfig = useApiConfig();
  const { status, siteId, cameraKey, dateRange, cameraNames, enabled = true } = options;
  const { tenant, authenticatedFetch } = apiConfig;

  const { dateFrom, dateTo } = toDateBounds(dateRange);

  const query = useInfiniteQuery<
    ListCompilationsResponse,
    Error,
    InfiniteData<ListCompilationsResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: ["compilations", "list", tenant?.tenantId, status, siteId, cameraKey, dateFrom, dateTo] as const,
    enabled: enabled && !!tenant,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((total, page) => total + page.data.length, 0);
      return loaded < lastPage.totalCount ? loaded : undefined;
    },
    queryFn: async ({ pageParam, signal }) => {
      if (!tenant) throw new Error("Tenant not ready");

      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listCompilationsEndpoint, {
          path: { tenantId: tenant.tenantId },
          query: {
            status,
            siteId,
            cameraKey,
            dateFrom,
            dateTo,
            limit: String(PAGE_SIZE),
            offset: String(pageParam),
          },
        });

      const response = await authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              message = json.message ?? text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with default message
        }
        throw new Error(message);
      }

      const data: unknown = await response.json();
      return listCompilationsEndpoint.responseSchema.parse(data);
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const items = useMemo<readonly CompilationListItem[]>(() => {
    const now = Date.now();
    return (query.data?.pages.flatMap((page) => page.data) ?? []).map((item) =>
      toViewModel(item, cameraNames, { stale: isStaleQueued(item, now) }),
    );
  }, [query.data, cameraNames]);

  return {
    items,
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error instanceof Error ? query.error : null,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
